import { useCallback, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BUDGET_TYPE_OPTIONS } from "../lib/constants";
import {
  BUDGET_BULK_MODE,
  BUDGET_BULK_MODE_OPTIONS,
  getDailyBudgetError,
  getMaxBudgetError,
  getMinBudgetError,
  isBudgetBulkPercentMode,
  MAX_BUDGET_BULK_MODE_OPTIONS,
  normalizeMaxBudgetInput,
  normalizeMinBudgetInput,
} from "../lib/helpers";
import {
  ABO_COLORS,
  numberFieldInputProps,
  selectFieldSx,
  textFieldSx,
} from "../lib/campaignAboStyles";

export const BULK_UPDATE_COLUMN = {
  DAILY_BUDGET: "daily_budget",
  MIN_BUDGET: "min_budget",
  MAX_BUDGET: "max_budget",
  BUDGET_TYPE: "budget_type",
  VALUE: "value",
};

const COLUMN_COPY = {
  [BULK_UPDATE_COLUMN.DAILY_BUDGET]: {
    title: "Bulk update daily budget",
    description: "Choose how to update daily budget for all selected campaigns.",
    fieldLabel: "Daily budget",
  },
  [BULK_UPDATE_COLUMN.MIN_BUDGET]: {
    title: "Bulk update min budget",
    description: "Choose how to update min budget for all campaigns in this table.",
    fieldLabel: "Min budget",
  },
  [BULK_UPDATE_COLUMN.MAX_BUDGET]: {
    title: "Bulk update max budget",
    description: "Choose how to set max budget for all campaigns in this table.",
    fieldLabel: "Max budget",
  },
  [BULK_UPDATE_COLUMN.BUDGET_TYPE]: {
    title: "Bulk update budget type",
    description: "Apply the same budget type to all campaigns in this slot.",
    fieldLabel: "Budget type",
  },
  [BULK_UPDATE_COLUMN.VALUE]: {
    title: "Bulk update value",
    description: "Choose how to set the budget value for all campaigns in this slot.",
    fieldLabel: "Value",
  },
};

function BulkInfoTooltip({ title }) {
  if (!title) return null;
  return (
    <Tooltip title={title} arrow placement="top">
      <InfoOutlinedIcon
        sx={{ fontSize: 16, color: ABO_COLORS.muted, cursor: "help", verticalAlign: "middle" }}
        aria-label="More information"
      />
    </Tooltip>
  );
}

export function CampaignAboBulkColumnHeader({
  label,
  infoTooltip,
  columnType,
  onApply,
  disabled = false,
  justify = "center",
}) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent={justify === "center" ? "center" : "flex-start"}
      component="span"
    >
      <span>{label}</span>
      <BulkInfoTooltip title={infoTooltip} />
      <CampaignAboBulkColumnUpdate
        columnType={columnType}
        onApply={onApply}
        disabled={disabled}
      />
    </Stack>
  );
}

export default function CampaignAboBulkColumnUpdate({
  columnType,
  onApply,
  disabled = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState({
    daily_budget: "",
    min_budget: "",
    max_budget: "",
    update_mode: BUDGET_BULK_MODE.SET_ABSOLUTE,
    budget_type: "PERCENT_INCREASE",
    value: "",
  });

  const open = Boolean(anchorEl);
  const copy = COLUMN_COPY[columnType] ?? COLUMN_COPY[BULK_UPDATE_COLUMN.VALUE];
  const isBudgetAmountColumn =
    columnType === BULK_UPDATE_COLUMN.DAILY_BUDGET ||
    columnType === BULK_UPDATE_COLUMN.MIN_BUDGET ||
    columnType === BULK_UPDATE_COLUMN.MAX_BUDGET ||
    columnType === BULK_UPDATE_COLUMN.VALUE;
  const isPercentMode = isBudgetBulkPercentMode(draft.update_mode);
  const isCopyMode = draft.update_mode === BUDGET_BULK_MODE.COPY_CAMPAIGN_BUDGET;
  const modeOptions =
    columnType === BULK_UPDATE_COLUMN.MAX_BUDGET ||
    columnType === BULK_UPDATE_COLUMN.VALUE
      ? MAX_BUDGET_BULK_MODE_OPTIONS
      : BUDGET_BULK_MODE_OPTIONS;
  const amountValue =
    columnType === BULK_UPDATE_COLUMN.DAILY_BUDGET
      ? draft.daily_budget
      : columnType === BULK_UPDATE_COLUMN.MIN_BUDGET
      ? draft.min_budget
      : columnType === BULK_UPDATE_COLUMN.VALUE
      ? draft.value
      : draft.max_budget;
  const amountError =
    columnType === BULK_UPDATE_COLUMN.DAILY_BUDGET &&
    draft.update_mode === BUDGET_BULK_MODE.SET_ABSOLUTE
      ? getDailyBudgetError(draft.daily_budget)
      : columnType === BULK_UPDATE_COLUMN.MIN_BUDGET &&
        draft.update_mode === BUDGET_BULK_MODE.SET_ABSOLUTE
      ? getMinBudgetError(draft.min_budget)
      : columnType === BULK_UPDATE_COLUMN.MAX_BUDGET &&
        draft.update_mode === BUDGET_BULK_MODE.SET_ABSOLUTE
      ? getMaxBudgetError(draft.max_budget)
      : "";

  const resetDraftForColumn = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      daily_budget: "",
      min_budget: "",
      max_budget: "",
      update_mode: BUDGET_BULK_MODE.SET_ABSOLUTE,
      budget_type: "PERCENT_INCREASE",
      value: "",
    }));
  }, []);

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    resetDraftForColumn();
  };

  const handleApply = () => {
    if (isBudgetAmountColumn) {
      if (isCopyMode) {
        onApply({ mode: draft.update_mode });
      } else if (amountValue === "" || amountError) {
        return;
      } else {
        onApply({ mode: draft.update_mode, value: amountValue });
      }
    } else if (columnType === BULK_UPDATE_COLUMN.BUDGET_TYPE) {
      onApply(draft.budget_type);
    }
    handleClose();
  };

  const applyDisabled = isBudgetAmountColumn
    ? isCopyMode
      ? false
      : amountValue === "" || Boolean(amountError)
    : false;

  return (
    <>
      <Tooltip title="Bulk update column" arrow placement="top">
        <span>
          <IconButton
            size="small"
            aria-label={`Bulk update ${copy.fieldLabel}`}
            onClick={handleOpen}
            disabled={disabled}
            sx={{
              p: 0.25,
              color: ABO_COLORS.muted,
              "&:hover": { color: "primary.main", bgcolor: "rgba(37, 99, 235, 0.08)" },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: isBudgetAmountColumn ? 320 : 280,
              borderRadius: 2,
              border: `1px solid ${ABO_COLORS.border}`,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            },
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: ABO_COLORS.title, mb: 0.5 }}>
          {copy.title}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: ABO_COLORS.muted, mb: 1.5, lineHeight: 1.4 }}>
          {copy.description}
        </Typography>

        {isBudgetAmountColumn && (
          <Stack spacing={1.5}>
            <FormControl fullWidth size="small" sx={selectFieldSx}>
              <InputLabel shrink>Update type</InputLabel>
              <Select
                label="Update type"
                notched
                value={draft.update_mode}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    update_mode: e.target.value,
                    daily_budget: "",
                    min_budget: "",
                    max_budget: "",
                    value: "",
                  }))
                }
              >
                {modeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!isCopyMode && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label={isPercentMode ? "Percent (%)" : copy.fieldLabel}
                value={amountValue}
                onChange={(e) => {
                  const nextValue =
                    columnType === BULK_UPDATE_COLUMN.MIN_BUDGET
                      ? normalizeMinBudgetInput(e.target.value)
                      : columnType === BULK_UPDATE_COLUMN.MAX_BUDGET
                      ? normalizeMaxBudgetInput(e.target.value)
                      : e.target.value === "" || Number.isNaN(Number(e.target.value))
                      ? e.target.value === ""
                        ? ""
                        : amountValue
                      : Number(e.target.value) < 0
                      ? "0"
                      : e.target.value;
                  setDraft((prev) => ({
                    ...prev,
                    daily_budget:
                      columnType === BULK_UPDATE_COLUMN.DAILY_BUDGET ? nextValue : prev.daily_budget,
                    min_budget:
                      columnType === BULK_UPDATE_COLUMN.MIN_BUDGET ? nextValue : prev.min_budget,
                    max_budget:
                      columnType === BULK_UPDATE_COLUMN.MAX_BUDGET ? nextValue : prev.max_budget,
                    value: columnType === BULK_UPDATE_COLUMN.VALUE ? nextValue : prev.value,
                  }));
                }}
                error={Boolean(amountError)}
                helperText={amountError}
                inputProps={{ min: 0, ...numberFieldInputProps }}
                sx={textFieldSx}
              />
            )}
          </Stack>
        )}

        {columnType === BULK_UPDATE_COLUMN.BUDGET_TYPE && (
          <FormControl fullWidth size="small" sx={selectFieldSx}>
            <InputLabel shrink>{copy.fieldLabel}</InputLabel>
            <Select
              label={copy.fieldLabel}
              notched
              value={draft.budget_type}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, budget_type: e.target.value }))
              }
            >
              {BUDGET_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClose}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={applyDisabled}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
          >
            Apply to all
          </Button>
        </Box>
      </Popover>
    </>
  );
}
